import { fetchAuthSession } from 'aws-amplify/auth';
import { checkAuthAndRedirect } from './authUtils';

export interface TenantData {
  email: string;
  tenantcode: string;
  fileCount: number;
}

export const tenantService = {
  apiUrl: `${process.env.REACT_APP_API_BASE_URL}/tenants`,
  
  // Get user's tenant data from S3
  getUserTenants: async (): Promise<TenantData[]> => {
    try {
      const isAuthenticated = await checkAuthAndRedirect();
      if (!isAuthenticated) return [];
      
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      const payload = session.tokens?.idToken?.payload;
      console.log('Full JWT payload:', payload);
      
      // Try different possible email fields
      const userEmail = (payload?.email || payload?.['cognito:username'] || payload?.username || payload?.sub) as string;
      console.log('Extracted user email:', userEmail);
      console.log('Available payload keys:', Object.keys(payload || {}));
      
      const urlWithEmail = `${tenantService.apiUrl}?email=${encodeURIComponent(userEmail || '')}`;
      
      const response = await fetch(urlWithEmail, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tenant data: ${response.status}`);
      }

      const responseText = await response.text();
      
      // Handle the double-encoded JSON from Lambda
      let allTenants = [];
      if (responseText.startsWith('{"statusCode"')) {
        const lambdaResponse = JSON.parse(responseText);
        const actualData = JSON.parse(lambdaResponse.body);
        allTenants = actualData.tenants || [];
      } else {
        const data = JSON.parse(responseText);
        allTenants = data.tenants || [];
      }
      
      // Filter tenants to only include those matching the logged-in user's email
      const userTenants = allTenants.filter((tenant: TenantData) => tenant.email === userEmail);
      console.log('Filtered tenants for user:', userTenants);
      return userTenants;
    } catch (error) {
      console.error('Error fetching tenant data:', error);
      return [];
    }
  },

  // Update user tenant mapping after file upload
  updateTenantMapping: async (tenantcode: string): Promise<void> => {
    try {
      const isAuthenticated = await checkAuthAndRedirect();
      if (!isAuthenticated) return;
      
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      const payload = session.tokens?.idToken?.payload;
      const userEmail = (payload?.email || payload?.['cognito:username'] || payload?.username || payload?.sub) as string;
      console.log('Update mapping - User email from JWT:', userEmail);
      
      await fetch(tenantService.apiUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ 
          email: userEmail,
          domain: tenantcode,
          action: 'increment'
        })
      });
    } catch (error) {
      console.error('Error updating tenant mapping:', error);
    }
  },

  // Delete tenant
  deleteTenant: async (tenantcode: string): Promise<void> => {
    try {
      const isAuthenticated = await checkAuthAndRedirect();
      if (!isAuthenticated) return;
      
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      const payload = session.tokens?.idToken?.payload;
      const userEmail = (payload?.email || payload?.['cognito:username'] || payload?.username || payload?.sub) as string;
      
      await fetch(tenantService.apiUrl, {
        method: 'DELETE',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({ 
          email: userEmail,
          tenantcode: tenantcode
        })
      });
    } catch (error) {
      console.error('Error deleting tenant:', error);
      throw error;
    }
  }
};